import { createApi } from "@reduxjs/toolkit/query/react";
import { ProductTypeEnum } from "../enums/ProductTypeEnum";
import { apiBaseQuery } from "../utils/apiBaseQuery";
import { ProductAccessoriesInterface } from "../interfaces/apiV2/product/ProductAccessoriesInterface";
import { ProductAssociatedInterface } from "../interfaces/apiV2/product/ProductAssociatedInterface";
import { ProductCategoriesInterface } from "../interfaces/apiV2/product/ProductCategoriesInterface";
import { ProductFilterGroupInterface } from "../interfaces/apiV2/product/ProductFilterOptionsInterface";
import { ProductIndexInterface } from "../interfaces/apiV2/product/ProductIndexInterface";
import { MetaInterface } from "../interfaces/apiV2/shared/MetaInterface";
import { createQueryString } from "../utils/createQueryString";
import { filterCategoriesByProductType } from "../utils/productRange/filterCategoriesByProductType";

const ASSOCIATION_TYPES = [
    "old_model",
    "up_sell",
    "previous_model",
    "add_on",
] as const;

const buildAssociationTypesQuery = (): string => {
    const params = new URLSearchParams();
    ASSOCIATION_TYPES.forEach((type) => params.append("types[]", type));
    return params.toString();
};

const buildProductIndexQuery = (params: {
    categoryIds: number[];
    search?: string;
    options?: string;
}): string => {
    const searchParams = new URLSearchParams();
    params.categoryIds.forEach((id) =>
        searchParams.append("filter[category_id][]", String(id)),
    );
    if (params.search) {
        searchParams.set("filter[search]", params.search);
    }
    if (params.options) {
        searchParams.set("filter[options]", params.options);
    }
    return searchParams.toString();
};

export const productApiSlice = createApi({
    reducerPath: "productApi",
    baseQuery: apiBaseQuery(),
    tagTypes: [
        "ProductAssociations",
        "ProductIndex",
        "ProductFilters",
        "ProductCategories",
    ],
    endpoints: (builder) => ({
        getProducts: builder.query<
            {
                data: ProductIndexInterface[];
                meta: MetaInterface;
            },
            {
                categoryIds: number[];
                search?: string;
                options?: string;
                countryId: number;
            }
        >({
            query: (params) => ({
                url: `apiV2/dealer/products?${buildProductIndexQuery(params)}`,
            }),
            providesTags: ["ProductIndex"],
        }),
        getProductFilterOptions: builder.query<
            { data: ProductFilterGroupInterface[] },
            void
        >({
            query: () => ({
                url: "apiV2/dealer/filter/options",
            }),
            transformResponse: (response: {
                data: Array<
                    ProductFilterGroupInterface & {
                        filter_tooltip?: string | null;
                    }
                >;
            }) => ({
                data: response.data.map((filter) => ({
                    ...filter,
                    filterTooltip:
                        filter.filterTooltip ?? filter.filter_tooltip ?? null,
                    options: filter.options.map((option) => ({
                        ...option,
                        id: option.id,
                    })),
                })),
            }),
            providesTags: ["ProductFilters"],
        }),
        exportProduct: builder.mutation<
            { blob: Blob; contentType: string | null },
            number
        >({
            query: (productId) => ({
                url: `apiV2/dealer/products/${productId}/export`,
                responseHandler: async (response: Response) => ({
                    blob: await response.blob(),
                    contentType: response.headers.get("content-type"),
                }),
            }),
        }),
        getProductCategories: builder.query<
            { data: ProductCategoriesInterface },
            {
                options?: string;
                productType: ProductTypeEnum;
                countryId: number;
            }
        >({
            query: (params) => ({
                url: `apiV2/dealer/categories?${createQueryString({
                    filter: {
                        options: params.options,
                        ...(params.productType === ProductTypeEnum.accessories
                            ? { code: ProductTypeEnum.accessories }
                            : {}),
                    },
                })}`,
            }),
            transformResponse: (
                response: { data: ProductCategoriesInterface },
                _meta,
                arg,
            ) => ({
                data: filterCategoriesByProductType(
                    response.data,
                    arg.productType,
                ),
            }),
            providesTags: ["ProductCategories"],
        }),
        getProductAccessories: builder.query<
            ProductAccessoriesInterface,
            { productId: number | string; countryId: number }
        >({
            query: ({ productId }) => ({
                url: `apiV2/dealer/products/${productId}/accessories`,
            }),
            transformResponse: (response: {
                data: ProductAccessoriesInterface;
            }) => response.data,
            providesTags: (_result, _error, { productId }) => [
                { type: "ProductAssociations", id: `${productId}-accessories` },
            ],
        }),
        getProductAssociations: builder.query<
            ProductAssociatedInterface,
            { productId: number | string; countryId: number }
        >({
            query: ({ productId }) => ({
                url: `apiV2/dealer/products/${productId}/associated?${buildAssociationTypesQuery()}`,
            }),
            transformResponse: (response: {
                data: ProductAssociatedInterface;
            }) => response.data,
            providesTags: (_result, _error, { productId }) => [
                { type: "ProductAssociations", id: productId },
            ],
        }),
    }),
});

export const {
    useGetProductsQuery,
    useGetProductFilterOptionsQuery,
    useGetProductCategoriesQuery,
    useGetProductAccessoriesQuery,
    useGetProductAssociationsQuery,
    useExportProductMutation,
} = productApiSlice;
